<template>
  <div class="backstage-gallery-manager">
    <div class="app-section">
      <div class="header-row">
        <div>
          <h2 class="title">Управление Backstage галереей</h2>
          <p class="subtitle">Общая галерея Backstage на сайте</p>
        </div>
        <button @click="showUploadModal = true" class="button">
          Загрузить новое изображение
        </button>
      </div>
    </div>

    <div class="app-section gallery-container">
      <h3 class="section-title">Изображения в галерее</h3>
      <div v-if="loading" class="state-block">Загрузка...</div>
      <div v-else-if="error" class="state-block error">{{ error }}</div>
      <div v-else-if="images.length === 0" class="state-block">Нет изображений в этой галерее</div>
      <div v-else class="images-grid">
        <div
          v-for="image in sortedImages"
          :key="image.id"
          class="image-card"
          :draggable="true"
          @dragstart="handleDragStart($event, image)"
          @dragover.prevent="handleDragOver($event, image)"
          @drop.prevent="handleDrop($event, image)"
          @dragend="handleDragEnd"
        >
          <div class="image-preview">
            <img
              v-if="image.url"
              :src="image.url"
              :alt="'Backstage image ' + image.id"
              class="gallery-image"
            />
            <div v-else class="image-placeholder">Изображение</div>
          </div>
          <div class="image-info">
            <div class="image-meta-row">
              <span>ID</span>
              <code>{{ image.id }}</code>
            </div>
            <div class="image-meta-row">
              <span>Позиция</span>
              <strong>{{ image.position }}</strong>
            </div>
            <div class="image-meta-row">
              <span>Файл ID</span>
              <code>{{ image.file_id }}</code>
            </div>
            <div class="image-meta-row">
              <span>Контекст</span>
              <strong>{{ image.context }} {{ image.context_id ? `(${image.context_id})` : '' }}</strong>
            </div>
          </div>
          <div class="image-actions">
            <button @click="deleteImage(image.id)" class="button button-danger !h-8 !px-3 !py-0 text-xs">Удалить</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <div v-if="showUploadModal" class="modal" @click.self="showUploadModal = false">
      <div class="modal-content">
        <h3 class="modal-title">Загрузить новое изображение</h3>
        <FileUpload
          label="Выберите изображение:"
          fileType="backstage_image"
          :onUpload="handleFileUpload"
          @file-uploaded="handleFileUploaded"
        />
        <div class="modal-actions">
          <button @click="showUploadModal = false" class="button button-outline">Отмена</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import {
  getBackstageImages,
  createBackstageImage,
  updateBackstageImage,
  deleteBackstageImage
} from '@/services/api';
import FileUpload from './FileUpload.vue';

export default {
  name: 'BackstageGalleryManager',
  components: {
    FileUpload
  },
  setup() {
    const currentContext = ref('main');
    const currentContextId = ref(null);
    const images = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const showUploadModal = ref(false);
    const uploadedFileId = ref(null);
    const draggedImage = ref(null);

    const sortedImages = computed(() => {
      return [...images.value].sort((a, b) => a.position - b.position);
    });

    const selectContext = () => {
      currentContext.value = 'main';
      currentContextId.value = null;
      loadImages();
    };

    const loadImages = async () => {
      if (!currentContext.value) return;

      loading.value = true;
      error.value = null;

      try {
        const params = {
          context: currentContext.value
        };

        if (currentContextId.value) {
          params.context_id = currentContextId.value;
        }

        const data = await getBackstageImages(params);
        images.value = data?.images || [];
      } catch (err) {
        error.value = 'Ошибка загрузки изображений: ' + err.message;
        console.error('Error loading backstage images:', err);
      } finally {
        loading.value = false;
      }
    };


    const handleFileUpload = async () => {
      // Обрабатывается компонентом FileUpload, ждём событие file-uploaded
    };

    const handleFileUploaded = async (data) => {
      if (data && data.id) {
        uploadedFileId.value = data.id;
        await createNewBackstageImage(data.id);
        showUploadModal.value = false;
      }
    };

    const createNewBackstageImage = async (fileId) => {
      if (!currentContext.value || !fileId) return;

      try {
        const newImage = {
          file_id: fileId,
          context: currentContext.value,
          context_id: currentContextId.value || null,
          position: images.value.length > 0 ? Math.max(...images.value.map(img => img.position)) + 1 : 0
        };

        const createdImage = await createBackstageImage(newImage);
        images.value.push(createdImage);
        uploadedFileId.value = null;
      } catch (err) {
        error.value = 'Ошибка создания изображения: ' + err.message;
        console.error('Error creating backstage image:', err);
      }
    };

    const deleteImage = async (imageId) => {
      if (!confirm('Вы уверены, что хотите удалить это изображение?')) return;

      try {
        await deleteBackstageImage(imageId);
        images.value = images.value.filter(img => img.id !== imageId);
      } catch (err) {
        error.value = 'Ошибка удаления изображения: ' + err.message;
        console.error('Error deleting backstage image:', err);
      }
    };

    const handleDragStart = (event, image) => {
      draggedImage.value = image;
      event.dataTransfer.setData('text/plain', image.id);
      event.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (event) => {
      event.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (event, targetImage) => {
      if (!draggedImage.value || draggedImage.value.id === targetImage.id) return;

      try {
        // Find the positions
        const draggedIndex = images.value.findIndex(img => img.id === draggedImage.value.id);
        const targetIndex = images.value.findIndex(img => img.id === targetImage.id);

        if (draggedIndex === -1 || targetIndex === -1) return;

        // Create a new array with updated positions
        const newImages = [...images.value];
        const [movedImage] = newImages.splice(draggedIndex, 1);
        newImages.splice(targetIndex, 0, movedImage);

        // Update positions
        const updatedImages = newImages.map((img, index) => ({
          ...img,
          position: index
        }));

        // Update each image position
        for (const img of updatedImages) {
          if (img.position !== images.value.find(i => i.id === img.id)?.position) {
            await updateBackstageImage(img.id, { position: img.position });
          }
        }

        // Refresh the list
        images.value = updatedImages;
      } catch (err) {
        error.value = 'Ошибка обновления позиций: ' + err.message;
        console.error('Error updating image positions:', err);
      }
    };

    const handleDragEnd = () => {
      draggedImage.value = null;
    };

    onMounted(() => {
      selectContext();
    });

    return {
      currentContext,
      currentContextId,
      images,
      loading,
      error,
      showUploadModal,
      sortedImages,
      selectContext,
      loadImages,
      handleFileUpload,
      handleFileUploaded,
      deleteImage,
      handleDragStart,
      handleDragOver,
      handleDrop,
      handleDragEnd
    };
  }
}
</script>

<style scoped>
.backstage-gallery-manager {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.subtitle {
  margin: 6px 0 0;
  color: hsl(var(--muted-foreground));
  font-size: 0.9rem;
}

.gallery-container {
  margin-top: 14px;
}

.section-title {
  margin: 0 0 14px;
  font-size: 1rem;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.state-block {
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  padding: 20px;
  text-align: center;
  font-size: 0.95rem;
}

.state-block.error {
  border-color: hsl(var(--destructive) / 0.35);
  background: hsl(var(--destructive) / 0.1);
  color: hsl(var(--destructive));
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  margin-top: 10px;
}

.image-card {
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  padding: 12px;
  background: hsl(var(--card));
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.image-card:hover {
  transform: translateY(-1px);
  border-color: hsl(var(--accent) / 0.35);
  box-shadow: 0 8px 20px hsl(var(--foreground) / 0.08);
}

.image-card.dragging {
  opacity: 0.5;
  border: 2px dashed hsl(var(--accent));
}

.image-preview {
  height: 180px;
  overflow: hidden;
  border-radius: 10px;
  margin-bottom: 10px;
  background: hsl(var(--muted));
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid hsl(var(--border));
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  color: hsl(var(--muted-foreground));
  text-align: center;
  padding: 20px;
}

.image-info {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.76rem;
  color: hsl(var(--muted-foreground));
}

.image-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
}

.image-meta-row code {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.72rem;
  color: hsl(var(--foreground));
  background: hsl(var(--muted));
  border: 1px solid hsl(var(--border));
  border-radius: 6px;
  padding: 2px 6px;
}

.image-actions {
  display: flex;
  justify-content: flex-end;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: hsl(var(--foreground) / 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 60;
  padding: 16px;
}

.modal-content {
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  padding: 20px;
  border-radius: 12px;
  width: 560px;
  max-width: 100%;
  box-shadow: 0 18px 40px hsl(var(--foreground) / 0.2);
}

.modal-title {
  margin: 0 0 8px;
  font-size: 1.05rem;
  color: hsl(var(--foreground));
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
  gap: 10px;
}
</style>
